/**
 * What this device saw, before it could have been told what to think.
 *
 * Verification is only worth something if it compares the reveal against the
 * commitments the player's own browser was shown *before* it contributed
 * entropy. If the check used the commitment the server sends alongside the
 * reveal, it would be comparing the server's two halves against each other and
 * would pass for any deal at all. So this module writes first and asks
 * questions later: the commitments land here the moment a `committing` snapshot
 * arrives, and the seed lands here before it is emitted, never after.
 *
 * `localStorage` is per-origin and survives reloads, which is what the check
 * needs; it can also be absent (private mode, storage disabled), so every
 * access is guarded and an in-memory copy keeps the current session checkable
 * even when nothing can be persisted.
 */
const KEY = 'cards.fairness.v2'
/** Enough for a long evening of rematches; the oldest entries fall off. */
const MAX_RECORDS = 200

export interface HandRecord {
  readonly matchId: string
  readonly handIndex: number
  /** The server-seed commitment, as first shown to this device. */
  readonly commitment: string
  /** The per-seat fill commitments, as first shown to this device. */
  readonly seatCommitments: readonly string[]
  readonly mySeat: number | null
  /** The 32 bytes this device generated for its seat, or null if it sent none. */
  readonly mySeed: string | null
  /** True once this device has emitted its contribution for this hand. */
  readonly contributed: boolean
  /** Set if the server later showed a *different* commitment for the same
   *  (match, hand). Keeping the first and flagging the change is the only way to
   *  notice a server that re-rolled a hand it had already committed to. */
  readonly changedCommitment?: string
}

type Store = Record<string, HandRecord>

const memory: Store = {}

const keyOf = (matchId: string, handIndex: number): string => `${matchId}:${handIndex}`

function read(): Store {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) return { ...(JSON.parse(raw) as Store), ...memory }
  } catch {
    /* fall through to the in-memory copy */
  }
  return { ...memory }
}

function write(store: Store): void {
  // Trim by insertion order — JS object key order is insertion order for
  // string keys, so the oldest hands go first.
  const keys = Object.keys(store)
  const trimmed: Store = keys.length > MAX_RECORDS
    ? Object.fromEntries(keys.slice(keys.length - MAX_RECORDS).map((k) => [k, store[k]]))
    : store
  for (const [k, v] of Object.entries(trimmed)) memory[k] = v
  try {
    localStorage.setItem(KEY, JSON.stringify(trimmed))
  } catch {
    /* the in-memory copy above still serves this session */
  }
}

/**
 * Records the commitments for a hand the first time they are seen, and returns
 * the record this device will verify against.
 *
 * First write wins, deliberately. A second, different commitment for the same
 * (match, hand) is not an update — it is either a bug or a server having
 * another go at a hand it already committed to, so the original is kept and the
 * discrepancy is recorded for the view to show.
 */
export function observeCommitment(
  matchId: string,
  handIndex: number,
  commitment: string,
  seatCommitments: readonly string[],
  mySeat: number | null,
): HandRecord {
  const store = read()
  const key = keyOf(matchId, handIndex)
  const existing = store[key]
  if (existing) {
    if (existing.commitment === commitment || existing.changedCommitment === commitment) {
      // Learning our seat later (joined mid-hand, or a snapshot before the
      // seat was known) is not a discrepancy.
      if (existing.mySeat === null && mySeat !== null) {
        const updated = { ...existing, mySeat }
        write({ ...store, [key]: updated })
        return updated
      }
      return existing
    }
    const flagged: HandRecord = { ...existing, changedCommitment: commitment }
    write({ ...store, [key]: flagged })
    return flagged
  }
  const fresh: HandRecord = {
    matchId,
    handIndex,
    commitment,
    seatCommitments: [...seatCommitments],
    mySeat,
    mySeed: null,
    contributed: false,
  }
  write({ ...store, [key]: fresh })
  return fresh
}

/** Stores the seed this device is about to send. Called *before* the emit: a
 *  seed that reached the server but not this store is a seed we could not later
 *  prove was ours. */
export function noteContribution(
  matchId: string,
  handIndex: number,
  mySeat: number,
  mySeed: string,
): HandRecord | null {
  const store = read()
  const key = keyOf(matchId, handIndex)
  const existing = store[key]
  if (!existing) return null
  const updated: HandRecord = { ...existing, mySeat, mySeed, contributed: true }
  write({ ...store, [key]: updated })
  return updated
}

export function readRecord(matchId: string, handIndex: number): HandRecord | null {
  return read()[keyOf(matchId, handIndex)] ?? null
}
